import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.heat';
import { TranslateService } from '@ngx-translate/core';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';
import { CollectsGeoPoint } from '../../../../services/collects-analytics/collects-analytics.model';
import { GetAdminOrganisationAreas } from '../../../../global.model';

@Component({
    selector: 'app-collects-heatmap',
    templateUrl: './collects-heatmap.component.html',
    styleUrls: ['./collects-heatmap.component.scss'],
})
export class CollectsHeatmapComponent implements OnChanges, OnDestroy {
    @Input() points: CollectsGeoPoint[] = [];
    @Input() areas: GetAdminOrganisationAreas[] = [];
    @Input() coveragePercent: number;
    @Input() matchedCount: number;

    private map: L.Map;
    private heatLayer: any;
    private mapReady = false;
    private defaultCenter: L.LatLngExpression = [45.5017, -73.5673];
    private defaultZoom = 13;

    selectedAreaId: string | null = null;

    constructor(private translate: TranslateService) {}

    get filteredPoints(): CollectsGeoPoint[] {
        if (!this.selectedAreaId) {
            return this.points;
        }
        return this.points.filter((p) => p.areaId === this.selectedAreaId);
    }

    get areaTabs(): { id: string | null; name: string }[] {
        const allTab = { id: null, name: this.translate.instant('pages.collections.analytics.heatmap_all') };
        const tabs = this.areas.map((a) => {
            const name = a.name as any;
            const resolved = typeof name === 'string' ? name : name[this.translate.currentLang] || name['fr'] || name['en'] || String(name);
            return { id: a._id, name: resolved };
        });
        return [allTab, ...tabs];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.points && this.points?.length > 0 && !this.mapReady) {
            setTimeout(() => this.initMap(), 100);
        } else if (this.mapReady && (changes.points || changes.areas)) {
            this.updateHeatmap();
        }
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    onAreaFilter(areaId: string | null): void {
        this.selectedAreaId = areaId;
        this.updateHeatmap();
    }

    private initMap(): void {
        const container = document.getElementById('collects-analytics-heatmap');
        if (!container || container.clientHeight === 0) {
            setTimeout(() => this.initMap(), 200);
            return;
        }

        if (this.map) {
            this.map.remove();
        }

        this.map = L.map('collects-analytics-heatmap', {
            zoomControl: false,
        }).setView(this.defaultCenter, this.defaultZoom);

        L.control.zoom({ position: 'topright' }).addTo(this.map);

        L.tileLayer(MAP_TILE_URL, {
            maxZoom: 18,
            minZoom: 3,
            attribution: MAP_TILE_ATTRIBUTION,
        }).addTo(this.map);

        this.mapReady = true;
        this.updateHeatmap();
    }

    private updateHeatmap(): void {
        if (!this.map) return;

        if (this.heatLayer) {
            this.map.removeLayer(this.heatLayer);
            this.heatLayer = null;
        }

        const pts = this.filteredPoints;
        if (pts.length === 0) return;

        const heatData: L.HeatLatLngTuple[] = pts.map((p) => [p.lat, p.lng, 1]);

        this.heatLayer = (L as any)
            .heatLayer(heatData, {
                radius: 25,
                blur: 15,
                maxZoom: 17,
                max: 1,
                gradient: { 0.4: '#06b6d4', 0.65: '#f59e0b', 1: '#ef4444' },
            })
            .addTo(this.map);

        const bounds = L.latLngBounds(pts.map((p) => [p.lat, p.lng] as L.LatLngTuple));
        this.map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
    }
}
